// LILAPE Game Configuration
// NEW: 1 TON = 1B LILAPE (updated 19 June 2026)

// Exchange rate
export const LILAPE_PER_TON = 1_000_000_000; // 1B LILAPE = 1 TON

// Balance tiers for win multipliers (in LILAPE) - reduced to prevent huge wins
export const BALANCE_MULTIPLIERS = [
  { minBalance: 20_000_000_000, multiplier: 1.5, name: 'Gold', color: '#FFD700', icon: '👑', tonEquiv: 20 },
  { minBalance: 10_000_000_000, multiplier: 1.3, name: 'Silver', color: '#C0C0C0', icon: '🥈', tonEquiv: 10 },
  { minBalance: 5_000_000_000, multiplier: 1.2, name: 'Bronze', color: '#CD7F32', icon: '🥉', tonEquiv: 5 },
  { minBalance: 0, multiplier: 1.0, name: 'Basic', color: '#888', icon: '🎮', tonEquiv: 0 },
];

// Get multiplier based on LILAPE balance
export const getBalanceMultiplier = (balance) => {
  const balanceNum = Number(balance) || 0;
  for (const tier of BALANCE_MULTIPLIERS) {
    if (balanceNum >= tier.minBalance) {
      return tier;
    }
  }
  return BALANCE_MULTIPLIERS[BALANCE_MULTIPLIERS.length - 1];
};

// VIP levels based on total TON spent
export const VIP_LEVELS = [
  { minSpent: 500, level: 'Diamond', freeSpins: 999, multiplier: 1.5, color: '#00CED1', icon: '💎', perks: ['Unlimited spins', 'x1.5 wins', 'Exclusive games'] },
  { minSpent: 100, level: 'Gold', freeSpins: 30, multiplier: 1.3, color: '#FFD700', icon: '🏆', perks: ['+10 daily spins', 'x1.3 wins', 'Jackpot access'] },
  { minSpent: 50, level: 'Silver', freeSpins: 25, multiplier: 1.2, color: '#C0C0C0', icon: '🥈', perks: ['+5 daily spins', 'x1.2 wins', 'Crash unlock'] },
  { minSpent: 10, level: 'Bronze', freeSpins: 22, multiplier: 1.1, color: '#CD7F32', icon: '🥉', perks: ['+2 daily spins', 'x1.1 wins'] },
  { minSpent: 0, level: 'Member', freeSpins: 20, multiplier: 1.0, color: '#888', icon: '🎮', perks: ['3 free spins/day'] },
];

// Get VIP level based on total TON spent
export const getVipLevel = (totalTonSpent) => {
  const spent = Number(totalTonSpent) || 0;
  for (const vip of VIP_LEVELS) {
    if (spent >= vip.minSpent) {
      return vip;
    }
  }
  return VIP_LEVELS[VIP_LEVELS.length - 1];
};

// Jackpot requirements (NEW: 100B entry, 1T prize)
export const JACKPOT_CONFIG = {
  minBalanceRequired: 100_000_000_000, // 100B LILAPE = 100 TON entry
  jackpotPrize: 1_000_000_000_000, // 1T LILAPE = 1000 TON
  jackpotPrizeInTon: 1000,
};

// Base spin cost
export const SPIN_COST = 20_000_000; // 20M LILAPE = 1 spin

// Bet amounts with win multipliers (reduced to prevent huge wins)
export const BET_AMOUNTS = [
  { lilape: 20_000_000, label: '20M', ton: 0.02, winMultiplier: 1, bonusLabel: '1 spin' },
  { lilape: 50_000_000, label: '50M', ton: 0.05, winMultiplier: 1, bonusLabel: 'base' },
  { lilape: 100_000_000, label: '100M', ton: 0.1, winMultiplier: 1.5, bonusLabel: 'x1.5 win' },
  { lilape: 500_000_000, label: '500M', ton: 0.5, winMultiplier: 2, bonusLabel: 'x2 win' },
];

// Buy LILAPE options (TON amounts)
export const BUY_OPTIONS = [
  { ton: 1, lilape: 1_000_000_000, label: '1 TON', lilapeLabel: '1B' },
  { ton: 5, lilape: 5_000_000_000, label: '5 TON', lilapeLabel: '5B' },
  { ton: 10, lilape: 10_000_000_000, label: '10 TON', lilapeLabel: '10B' },
];

// Calculate spins available for a bet amount
export const calculateSpins = (balance, betAmount) => {
  if (!balance || !betAmount || betAmount <= 0) return 0;
  return Math.floor(balance / betAmount);
};

// Get win multiplier for bet amount
export const getWinMultiplier = (betAmount) => {
  const bet = BET_AMOUNTS.find(b => b.lilape === betAmount);
  return bet ? bet.winMultiplier : 1;
};

// Slot machine prizes - PROPORTIONAL to bet (multiplier x bet)
// House edge ~15-20%. Total win chance ~30%
export const SLOT_PRIZES = [
  { symbols: '🍒🍒🍒', chance: 0.12, multiplier: 0.5, label: 'Cherry Win!' },      // 12% - x0.5 (small return)
  { symbols: '🍊🍊🍊', chance: 0.08, multiplier: 1, label: 'Orange Boost!' },      // 8% - x1 (break even)
  { symbols: '🍌🍌🍌', chance: 0.05, multiplier: 2, label: 'Banana Blast!' },      // 5% - x2
  { symbols: '🐒🐒🐒', chance: 0.02, multiplier: 5, label: 'Monkey Match!' },      // 2% - x5
  { symbols: '💎💎💎', chance: 0.008, multiplier: 10, label: 'Diamond Deal!' },    // 0.8% - x10
  { symbols: '👑👑👑', chance: 0.003, multiplier: 50, label: 'Crown Jackpot!' },   // 0.3% - x50
  { symbols: '7️⃣7️⃣7️⃣', chance: 0.001, multiplier: 100, label: 'MEGA WIN!' },     // 0.1% - x100
];
// Total win chance: ~28.2%, No win: ~71.8%

// Calculate prize based on bet and multiplier
export const calculatePrize = (bet, multiplier) => {
  return Math.floor(bet * multiplier);
};

// Presale stages (NEW: 1 TON = 1B at Stage 1, 500M at Stage 10)
export const PRESALE_STAGES = [
  { stage: 1, rate: 1_000_000_000, rateLabel: '1B', discount: 50, allocation: 6.3e12, status: 'active' },
  { stage: 2, rate: 950_000_000, rateLabel: '950M', discount: 45, allocation: 6.3e12, status: 'upcoming' },
  { stage: 3, rate: 900_000_000, rateLabel: '900M', discount: 40, allocation: 6.3e12, status: 'upcoming' },
  { stage: 4, rate: 850_000_000, rateLabel: '850M', discount: 35, allocation: 6.3e12, status: 'upcoming' },
  { stage: 5, rate: 800_000_000, rateLabel: '800M', discount: 30, allocation: 6.3e12, status: 'upcoming' },
  { stage: 6, rate: 750_000_000, rateLabel: '750M', discount: 25, allocation: 6.3e12, status: 'upcoming' },
  { stage: 7, rate: 700_000_000, rateLabel: '700M', discount: 20, allocation: 6.3e12, status: 'upcoming' },
  { stage: 8, rate: 650_000_000, rateLabel: '650M', discount: 15, allocation: 6.3e12, status: 'upcoming' },
  { stage: 9, rate: 550_000_000, rateLabel: '550M', discount: 5, allocation: 6.3e12, status: 'upcoming' },
  { stage: 10, rate: 500_000_000, rateLabel: '500M', discount: 0, allocation: 6.3e12, status: 'listing' },
];

// Get current presale stage
export const getCurrentStage = () => {
  return PRESALE_STAGES.find(s => s.status === 'active') || PRESALE_STAGES[0];
};

// Tokenomics (unchanged)
export const TOKENOMICS = {
  totalSupply: 420_000_000_000_000, // 420T
  distribution: [
    { name: 'Prize Pool', percentage: 25, amount: 105e12, color: '#8B5CF6', icon: '🎰' },
    { name: 'Presale', percentage: 15, amount: 63e12, color: '#F59E0B', icon: '💎' },
    { name: 'Liquidity', percentage: 15, amount: 63e12, color: '#3B82F6', icon: '💧' },
    { name: 'Staking', percentage: 15, amount: 63e12, color: '#10B981', icon: '🏦' },
    { name: 'Community', percentage: 15, amount: 63e12, color: '#EC4899', icon: '👥' },
    { name: 'Marketing', percentage: 10, amount: 42e12, color: '#F97316', icon: '📢' },
    { name: 'Team', percentage: 5, amount: 21e12, color: '#6B7280', icon: '👔' },
  ],
  tax: 0,
  burnRate: 50, // 50% of house edge burned
};

// Format large numbers
export const formatLilape = (amount) => {
  const num = Number(amount) || 0;
  if (num >= 1e12) return `${(num / 1e12).toFixed(1)}T`;
  if (num >= 1e9) return `${(num / 1e9).toFixed(1)}B`;
  if (num >= 1e6) return `${(num / 1e6).toFixed(1)}M`;
  if (num >= 1e3) return `${(num / 1e3).toFixed(1)}K`;
  return num.toString();
};

// Convert LILAPE to TON
export const lilapeToTon = (lilape) => {
  return Number(lilape) / LILAPE_PER_TON;
};

// Convert TON to LILAPE
export const tonToLilape = (ton) => {
  return Number(ton) * LILAPE_PER_TON;
};
